import React, { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, doc, serverTimestamp, writeBatch } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { db } from "../../firebase";
import { calculatePoints } from "../../utils/customerEntryPoints";
import { formatCustomerEntryActorLabel, customerEntryActorMatchesFilter, resolveCustomerEntryEnteredBy } from "../../utils/customerEntryProfile";
import { writeAuditLog } from "../../utils/auditLog";
import { useCustomerEntries } from "./customerEntriesStore";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const RENDER_PAGE_SIZE = 200;
const IMPORT_BATCH_SIZE = 400;

const inrFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

function formatInr(n) {
  return `₹${inrFmt.format(Math.round(Number(n) || 0))}`;
}

const selectClass =
  "rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/30 disabled:bg-gray-100 disabled:text-gray-500";

export default function CustomerEntryReportTab({ onNewEntry }) {
  const navigate = useNavigate();
  const { rows: allRows, loading, error: loadError } = useCustomerEntries();
  const docs = useMemo(() => allRows.filter((r) => !r.voided), [allRows]);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const [renderLimit, setRenderLimit] = useState(RENDER_PAGE_SIZE);
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth() + 1);
  const [showAllMonths, setShowAllMonths] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [sortMode, setSortMode] = useState("points"); // points | latest | amount
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);

  const filteredByMonth = useMemo(() => {
    if (showAllMonths) return docs;
    const from = new Date(selectedYear, selectedMonth - 1, 1).getTime();
    const to = new Date(selectedYear, selectedMonth, 1).getTime();
    return docs.filter((r) => r.ts >= from && r.ts < to);
  }, [docs, showAllMonths, selectedYear, selectedMonth]);

  const userList = useMemo(() => {
    const s = new Set();
    docs.forEach((r) => {
      const e = formatCustomerEntryActorLabel(r.data.enteredBy?.toString()?.trim());
      if (e) s.add(e);
    });
    return [...s].sort();
  }, [docs]);

  const entries = useMemo(() => {
    if (!selectedUser) return filteredByMonth;
    return filteredByMonth.filter((r) => customerEntryActorMatchesFilter(r.data.enteredBy, selectedUser));
  }, [filteredByMonth, selectedUser]);

  const { customerList, totalAmount, totalPoints } = useMemo(() => {
    const map = new Map();
    let ta = 0;
    let tp = 0;
    for (const row of entries) {
      const data = row.data;
      const name = data.customerName?.toString() ?? "-";
      const phone = data.phone?.toString() ?? "-";
      const amount = Number(data.amount) || 0;
      const points = data.points != null ? Number(data.points) : calculatePoints(amount);
      ta += amount;
      tp += points;
      if (name === "-") continue;

      const lastTs = row.ts;

      if (map.has(name)) {
        const c = map.get(name);
        c.totalAmount += amount;
        c.totalPoints += points;
        c.count += 1;
        // If this entry is newer, prefer its phone + update last timestamp.
        if (lastTs > c.lastTs) {
          c.phone = phone;
          c.lastTs = lastTs;
        }
      } else {
        map.set(name, {
          name,
          phone,
          totalAmount: amount,
          totalPoints: points,
          count: 1,
          lastTs,
        });
      }
    }

    const list = [...map.values()].sort((a, b) => {
      if (sortMode === "latest") {
        const lt = (b.lastTs || 0) - (a.lastTs || 0);
        if (lt !== 0) return lt;
        const pc = b.totalPoints - a.totalPoints;
        if (pc !== 0) return pc;
        return b.totalAmount - a.totalAmount;
      }
      if (sortMode === "amount") {
        const ac = b.totalAmount - a.totalAmount;
        if (ac !== 0) return ac;
        const pc = b.totalPoints - a.totalPoints;
        if (pc !== 0) return pc;
        return (b.lastTs || 0) - (a.lastTs || 0);
      }
      // points (default)
      const pc = b.totalPoints - a.totalPoints;
      if (pc !== 0) return pc;
      return b.totalAmount - a.totalAmount;
    });
    return { customerList: list, totalAmount: ta, totalPoints: tp };
  }, [entries, sortMode]);

  const displayList = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return customerList;
    return customerList.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || String(c.phone || "").toLowerCase().includes(q)
    );
  }, [customerList, deferredSearch]);

  useEffect(() => {
    setRenderLimit(RENDER_PAGE_SIZE);
  }, [showAllMonths, selectedYear, selectedMonth, selectedUser, sortMode, deferredSearch]);

  const visibleList = useMemo(() => displayList.slice(0, renderLimit), [displayList, renderLimit]);

  const exportExcel = () => {
    if (exporting) return;
    setExporting(true);
    try {
      const wb = XLSX.utils.book_new();
      const summary = [
        ["Customer Report Summary"],
        [],
        ["Total Customers", String(customerList.length)],
        ["Total Entries", String(entries.length)],
        ["Total Amount", `₹${totalAmount.toFixed(2)}`],
        ["Total Points", String(Math.round(totalPoints))],
        [],
        ["Generated On", new Date().toLocaleString("en-IN")],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");

      const custRows = [
        ["S.No", "Customer Name", "Phone", "Total Entries", "Total Amount (₹)", "Total Points"],
        ...customerList.map((c, i) => [
          i + 1,
          c.name,
          c.phone,
          c.count,
          c.totalAmount,
          c.totalPoints,
        ]),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(custRows), "Customer Summary");

      const entryRows = [
        ["S.No", "Date", "Time", "Customer Name", "Phone", "Invoice No", "Amount (₹)", "Points", "Created By"],
      ];
      const sorted = [...entries].sort((a, b) => {
        const pa = Number(a.data.points) || calculatePoints(Number(a.data.amount) || 0);
        const pb = Number(b.data.points) || calculatePoints(Number(b.data.amount) || 0);
        if (pb !== pa) return pb - pa;
        return b.ts - a.ts;
      });
      sorted.forEach((row, i) => {
        const data = row.data;
        const name = data.customerName ?? "-";
        const phone = data.phone ?? "-";
        const amt = Number(data.amount) || 0;
        const pts = data.points != null ? Number(data.points) : calculatePoints(amt);
        const cr = row.ts ? new Date(row.ts) : null;
        const dateStr = cr ? cr.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-";
        const timeStr = cr ? cr.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "-";
        const createdBy = formatCustomerEntryActorLabel(
          data.enteredBy?.toString() || data.createdByEmail?.toString() || data.createdByUid?.toString() || "-"
        );
        const pairs = data.invoiceAmountPairs;
        if (Array.isArray(pairs) && pairs.length) {
          pairs.forEach((pair) => {
            const inv = (pair?.invoice ?? pair?.invoiceNumber ?? "").toString().trim();
            const amount = Number(pair?.amount) || 0;
            entryRows.push([
              i + 1,
              dateStr,
              timeStr,
              name,
              phone,
              inv || "-",
              amount,
              pts,
              createdBy,
            ]);
          });
        } else {
          let inv = "-";
          if (Array.isArray(data.invoiceNumbers)) inv = data.invoiceNumbers.join(", ");
          else if (data.invoiceNo) inv = String(data.invoiceNo);
          entryRows.push([i + 1, dateStr, timeStr, name, phone, inv, amt, pts, createdBy]);
        }
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(entryRows), "All Entries");

      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const name = `customer_report_${new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "_")}.xlsx`;
      saveAs(blob, name);
      toast.success("Excel exported");
    } catch (e) {
      console.error(e);
      toast.error(`Export failed: ${e?.message || e}`);
    } finally {
      setExporting(false);
    }
  };

  const years = useMemo(() => {
    const s = new Set();
    const y0 = new Date().getFullYear();
    for (let y = y0; y >= y0 - 5; y--) s.add(y);
    docs.forEach((r) => {
      if (r.ts) s.add(new Date(r.ts).getFullYear());
    });
    return Array.from(s).sort((a, b) => b - a);
  }, [docs]);

  const runBulkImport = async (file) => {
    if (!file || importBusy) return;
    setImportBusy(true);
    try {
      let wb;
      if (file.name.toLowerCase().endsWith(".csv")) {
        const text = await file.text();
        wb = XLSX.read(text, { type: "string" });
      } else {
        const buf = await file.arrayBuffer();
        wb = XLSX.read(buf, { type: "array" });
      }
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { raw: false, defval: "" });
      if (!rows.length) {
        toast.error("No rows in file");
        return;
      }
      const keys = Object.keys(rows[0]);
      const norm = (k) =>
        String(k || "")
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
      const pick = (hints) => {
        for (const h of hints) {
          const f = keys.find((k) => norm(k).includes(h) || norm(k) === h);
          if (f) return f;
        }
        return null;
      };
      const kName = pick(["customername", "name", "customer"]);
      const kPhone = pick(["phone", "mobile", "contact"]);
      const kInv = pick(["invoice", "invoiceno", "inv", "bill"]);
      const kAmt = pick(["amount", "amt", "value", "total"]);
      if (!kName || !kPhone || !kInv || !kAmt) {
        toast.error("Need columns: customer name, phone, invoice, amount (headers in first row)");
        return;
      }

      const auth = getAuth();
      const user = auth.currentUser;
      const enteredBy = await resolveCustomerEntryEnteredBy(auth, db);
      const role = (() => {
        try {
          return JSON.parse(localStorage.getItem("user") || "{}")?.role || "unknown";
        } catch {
          return "unknown";
        }
      })();

      let ok = 0;
      let skipped = 0;
      const col = collection(db, "customer_entries");
      let batch = writeBatch(db);
      let pending = 0;
      for (const row of rows) {
        const customerName = String(row[kName] ?? "").trim();
        const phone = String(row[kPhone] ?? "").trim();
        const inv = String(row[kInv] ?? "").trim();
        const amount = parseFloat(String(row[kAmt] ?? "").replace(/,/g, "")) || 0;
        if (!customerName || !phone || !inv || amount <= 0) {
          skipped++;
          continue;
        }
        const pts = calculatePoints(amount);
        batch.set(doc(col), {
          customerName,
          phone,
          invoiceNo: inv,
          invoiceNumbers: [inv],
          invoiceAmountPairs: [{ invoice: inv, amount }],
          amount,
          points: pts,
          createdAt: serverTimestamp(),
          createdByUid: user?.uid ?? null,
          createdByEmail: user?.email ?? null,
          enteredBy,
          importBatch: true,
        });
        pending++;
        ok++;
        if (pending >= IMPORT_BATCH_SIZE) {
          await batch.commit();
          batch = writeBatch(db);
          pending = 0;
        }
      }
      if (pending > 0) await batch.commit();
      try {
        await writeAuditLog({
          entity: "customer_entry",
          entityId: "bulk",
          action: "import",
          extra: { imported: ok, skipped, fileName: file.name },
          uid: user?.uid ?? null,
          email: user?.email ?? null,
          displayName: user?.displayName ?? null,
          role,
        });
      } catch {
        /* ignore */
      }
      toast.success(`Imported ${ok} row(s)${skipped ? ` · ${skipped} skipped` : ""}`);
      setImportOpen(false);
    } catch (e) {
      console.error(e);
      toast.error(`Import failed: ${e?.message || e}`);
    } finally {
      setImportBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] flex-1 items-center justify-center">
        <div className="h-9 w-9 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50/90 px-4 py-4 text-sm text-red-800 shadow-sm">
        <p className="font-semibold">Could not load customer entries</p>
        <p className="mt-1 text-red-700/95">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-3">
      <div className="shrink-0 rounded-xl border border-gray-200/80 bg-white p-3 shadow-sm sm:p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
            Filters
            <span className="ml-1.5 font-normal normal-case text-gray-400">
              · {entries.length} {entries.length === 1 ? "entry" : "entries"}
            </span>
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => navigate("/reports/customer-entry/monthly")}
              className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-800 shadow-sm transition hover:bg-indigo-100"
            >
              Monthly report
            </button>
            <button
              type="button"
              onClick={() => navigate("/reports/customer-entry/gifts")}
              className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-xs font-semibold text-violet-800 shadow-sm transition hover:bg-violet-100"
            >
              Gift settings
            </button>
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50"
            >
              Import
            </button>
            <button
              type="button"
              disabled={exporting || entries.length === 0}
              onClick={exportExcel}
              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
            >
              <svg className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>{exporting ? "Exporting…" : "Export"}</span>
            </button>
            {typeof onNewEntry === "function" && (
              <button
                type="button"
                onClick={onNewEntry}
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New entry
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            className={selectClass}
            disabled={showAllMonths}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
          <select
            className={selectClass}
            disabled={showAllMonths}
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <label className="flex cursor-pointer items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={showAllMonths}
              onChange={(e) => setShowAllMonths(e.target.checked)}
            />
            All time
          </label>
          <select
            className={`${selectClass} min-w-[140px] flex-1 sm:flex-initial`}
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
          >
            <option value="">All users</option>
            {userList.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>

          <select
            className={`${selectClass} min-w-[170px]`}
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
          >
            <option value="points">Top points</option>
            <option value="latest">Latest</option>
            <option value="amount">Highest amount</option>
          </select>
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-3 gap-2 rounded-xl border border-gray-200/80 bg-white p-3 shadow-sm sm:gap-3 sm:p-4">
        <div className="text-center">
          <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Customers</p>
          <p className="mt-0.5 text-base font-bold text-indigo-600 sm:text-lg">{customerList.length}</p>
        </div>
        <div className="border-x border-gray-100 text-center">
          <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Amount</p>
          <p className="mt-0.5 text-base font-bold text-emerald-600 sm:text-lg tabular-nums">{formatInr(totalAmount)}</p>
        </div>
        <div className="text-center">
          <p className="text-[10px] font-medium uppercase tracking-wide text-gray-500">Points</p>
          <p className="mt-0.5 text-base font-bold text-indigo-600 sm:text-lg">{Math.round(totalPoints)}</p>
        </div>
      </div>

      <div className="shrink-0 space-y-1.5">
        <div className="relative">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            id="ce-search-input"
            type="search"
            placeholder="Search name or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-xs text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/30"
          />
        </div>
        {search.trim() ? (
          <p className="text-[11px] text-gray-500">
            Showing <span className="font-semibold text-gray-800">{displayList.length}</span> of{" "}
            <span className="font-semibold text-gray-800">{customerList.length}</span> customers in this view
          </p>
        ) : null}
      </div>

      {/* Scrollable list — fills remaining viewport height */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-2xl border border-gray-200/60 bg-white/60 p-3 sm:p-4 lg:min-h-[calc(100dvh-20rem)]">
        {entries.length === 0 ? (
          <div className="flex min-h-[30vh] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-200 bg-white/90 px-4 py-10 text-center">
            <p className="text-sm font-medium text-gray-700">
              {showAllMonths ? "No customer entries yet" : `No entries for ${MONTHS[selectedMonth - 1]} ${selectedYear}`}
            </p>
            <ul className="max-w-sm list-inside list-disc text-left text-xs text-gray-500">
              <li>Use <span className="font-medium text-gray-700">New entry</span> to add a purchase.</li>
              <li>Try <span className="font-medium text-gray-700">All time</span> or another month if data exists.</li>
            </ul>
          </div>
        ) : displayList.length === 0 ? (
          <div className="flex min-h-[30vh] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white/90 px-4 py-10 text-center text-sm text-gray-500">
            <p>No matches for your search</p>
            <p className="text-xs text-gray-400">Clear the search box or widen your filters.</p>
          </div>
        ) : (
          <>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {visibleList.map((c) => (
              <li key={c.name} className="min-w-0">
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/reports/customer-entry/detail/${encodeURIComponent(c.name)}`)
                  }
                  className="group flex h-full w-full flex-col rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50/50 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-semibold text-indigo-700 underline decoration-indigo-200 underline-offset-2 group-hover:text-indigo-800 line-clamp-2">
                      {c.name}
                    </span>
                    {c.count > 1 && (
                      <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-800">
                        {c.count}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-[11px] text-gray-500">{c.phone}</p>
                  {c.lastTs > 0 ? (
                    <p className="mt-1 text-[10px] text-gray-400">
                      Last:{" "}
                      {new Date(c.lastTs).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </p>
                  ) : null}
                  <div className="mt-2 flex items-center justify-between border-t border-gray-100 pt-1.5">
                    <span className="text-xs font-semibold text-emerald-700 tabular-nums">{formatInr(c.totalAmount)}</span>
                    <span className="text-xs font-medium text-indigo-600 tabular-nums">{Math.round(c.totalPoints)} pts</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {displayList.length > visibleList.length ? (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setRenderLimit((n) => n + RENDER_PAGE_SIZE)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                Show more ({displayList.length - visibleList.length} more customers)
              </button>
            </div>
          ) : null}
          </>
        )}
      </div>

      {importOpen && (
        <div
          className="fixed inset-0 z-[1300] flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-gray-900">Bulk import</h3>
            <p className="mt-1 text-xs text-gray-500">
              Excel (.xlsx) or CSV: first row headers — <strong>customer name</strong>, <strong>phone</strong>,{" "}
              <strong>invoice</strong>, <strong>amount</strong>. One row per entry.
            </p>
            <label className="mt-4 flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-sm text-gray-600 hover:bg-gray-100">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                className="hidden"
                disabled={importBusy}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) runBulkImport(f);
                  e.target.value = "";
                }}
              />
              {importBusy ? "Importing…" : "Choose file"}
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setImportOpen(false)}
                disabled={importBusy}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
